import { DatePicker } from 'antd'
import React, { useEffect, useState } from 'react'

const hideSpinnerStyles = `
  .number-input-no-spinner::-webkit-outer-spin-button,
  .number-input-no-spinner::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .number-input-no-spinner[type=number] {
    -moz-appearance: textfield;
  }
`

const combinedStyles = `
  ${hideSpinnerStyles}

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style')
  styleSheet.textContent = combinedStyles
  document.head.appendChild(styleSheet)
}

interface PropertyType {
  id: number
  name: string
  icon_url: string
  display_order: number
}

interface PropertyFormData {
  propertyType: string
  unitSize: string
  bedrooms: string
  bathrooms: string
  floorNumber: string
  roomNumber: string
  roomPosition: string
  hasBalcony: boolean | null
  isPetFriendly: boolean | null
  maxGuests: string
  availability: string
  availableDate: any
  propertyPhotos: string[]
  videoTour: string | null
  floorPlan: string | null
  monthlyPrice: string
  sixMonthPrice: string
  yearlyPrice: string
  securityDeposit: string
  useHaupAgent: boolean
  listingTitle: string
  shortDescription: string
  uniqueSellingPoints: string[]
}

interface Step1Props {
  onNext: () => void
  onPrevious: () => void
  onStepProgress: (progress: number) => void
  formData: PropertyFormData
  updateFormData: (data: Partial<PropertyFormData>) => void
}

const Step1: React.FC<Step1Props> = ({
  onNext,
  onPrevious,
  onStepProgress,
  formData,
  updateFormData,
}) => {
  const [progress, setProgress] = useState(0)
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPropertyTypes = async () => {
      try {
        setLoading(true)
        const response = await fetch(`${process.env.REACT_APP_TS_RESTFUL_API_URL}/property/types`)
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }

        const data = await response.json()
        if (data.result && data.data) {
          setPropertyTypes(
            data.data.sort((a: PropertyType, b: PropertyType) => a.display_order - b.display_order),
          )
        } else {
          throw new Error(data.message || 'Failed to fetch property types')
        }
      } catch (err: any) {
        setError(err.message || 'An error occurred')
      } finally {
        setLoading(false)
      }
    }

    fetchPropertyTypes()
  }, [])

  const bedroomOptions = ['Studio', '1 bedroom', '2 bedrooms', '2+ bedrooms']
  const bathroomOptions = ['1 bathroom', '2 bathrooms', '3 bathrooms', '3+ bathrooms']
  const roomPositions = ['Corner unit', 'Near elevator', 'Middle unit', 'End of corridor']
  const availabilityOptions = ['Available immediately', 'Available after this date']

  const calculateProgress = () => {
    const fields = [
      formData.propertyType,
      formData.unitSize,
      formData.bedrooms,
      formData.bathrooms,
      formData.floorNumber,
      formData.roomNumber,
      formData.roomPosition,
      formData.hasBalcony !== null,
      formData.isPetFriendly !== null,
      formData.maxGuests,
      formData.availability,
      formData.availability === 'Available after this date' ? formData.availableDate : true,
    ]

    const filledFields = fields.filter((field) => field && field !== '').length
    const progressPercentage = Math.round((filledFields / fields.length) * 100)
    return progressPercentage
  }

  useEffect(() => {
    const newProgress = calculateProgress()
    setProgress(newProgress)
    onStepProgress(newProgress)
  }, [formData, onStepProgress])

  const handlePropertyTypeSelect = (typeId: number, typeName: string) => {
    updateFormData({ propertyType: typeName.toLowerCase() })
  }

  const handleInputChange = (field: string, value: any) => {
    updateFormData({ [field]: value })
  }

  const handleRadioChange = (field: string, value: string) => {
    updateFormData({ [field]: value })
  }

  const handleBooleanChange = (field: string, value: boolean) => {
    updateFormData({ [field]: value })
  }

  const isNextDisabled = progress < 100

  return (
    <div
      style={{
        backgroundColor: '#fff',
        minHeight: '100vh',
        fontFamily: 'Poppins, sans-serif',
      }}
    >
      <div style={{ padding: '24px 16px' }}>
        <div style={{ marginBottom: '32px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '400',
            }}
          >
            Property type
          </label>

          {loading ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
                marginBottom: '16px',
              }}
            >
              {[1, 2, 3, 4].map((index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '16px 8px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#f5f5f5',
                    minHeight: '80px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                >
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                      marginBottom: '8px',
                    }}
                  />
                  <div
                    style={{
                      width: '60px',
                      height: '12px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                    }}
                  />
                </div>
              ))}
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
              Error: {error}
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  propertyTypes.length <= 3
                    ? `repeat(${propertyTypes.length}, 1fr)`
                    : 'repeat(3, 1fr)',
                gap: '12px',
                marginBottom: '16px',
              }}
            >
              {propertyTypes.map((type) => {
                const isSelected = formData.propertyType === type.name.toLowerCase()
                return (
                  <button
                    key={type.id}
                    onClick={() => handlePropertyTypeSelect(type.id, type.name)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '16px 8px',
                      border: isSelected ? '2px solid #4C4848' : '1px solid #e0e0e0',
                      borderRadius: '8px',
                      backgroundColor: isSelected ? '#F2E9DF' : '#fff',
                      cursor: 'pointer',
                      fontFamily: 'Poppins, sans-serif',
                      minHeight: '80px',
                      transition: 'all 0.2s ease',
                      transform: 'scale(1)',
                      boxShadow: isSelected ? '0 2px 8px rgba(76, 72, 72, 0.15)' : 'none',
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.transform = 'scale(0.95)'
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.transform = 'scale(1)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)'
                    }}
                  >
                    <img
                      src={type.icon_url}
                      alt={type.name}
                      style={{
                        width: '32px',
                        height: '32px',
                        marginBottom: '8px',
                        filter: isSelected
                          ? 'brightness(0) saturate(100%) invert(28%) sepia(0%) saturate(0%) hue-rotate(138deg) brightness(95%) contrast(88%)'
                          : 'none',
                      }}
                    />
                    <span style={{ fontSize: '12px', color: isSelected ? '#4C4848' : '#333' }}>
                      {type.name}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '8px',
              fontWeight: '400',
            }}
          >
            Unit size (sqm)
          </label>
          <input
            type="number"
            placeholder="Enter the unit size of your property"
            value={formData.unitSize}
            onChange={(e) => handleInputChange('unitSize', e.target.value)}
            onWheel={(e) => e.currentTarget.blur()}
            onKeyDown={(e) => {
              if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                e.preventDefault()
              }
            }}
            className="number-input-no-spinner"
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '400',
            }}
          >
            No. of bedrooms
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {bedroomOptions.map((option) => (
              <label
                key={option}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: formData.bedrooms === option ? '#4C4848' : '#333',
                  padding: '16px',
                  border: '1px solid #EDEFF4',
                  borderRadius: '8px',
                  backgroundColor: formData.bedrooms === option ? '#F2E9DF' : '#fff',
                  borderColor: formData.bedrooms === option ? '#4C4848' : '#EDEFF4',
                }}
              >
                <input
                  type="radio"
                  name="bedrooms"
                  value={option}
                  checked={formData.bedrooms === option}
                  onChange={(e) => handleRadioChange('bedrooms', e.target.value)}
                  style={{ marginRight: '8px', accentColor: '#4C4848' }}
                />
                {option}
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '400',
            }}
          >
            No. of bathroom
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {bathroomOptions.map((option) => (
              <label
                key={option}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: formData.bathrooms === option ? '#4C4848' : '#333',
                  padding: '16px',
                  border: '1px solid #EDEFF4',
                  borderRadius: '8px',
                  backgroundColor: formData.bathrooms === option ? '#F2E9DF' : '#fff',
                  borderColor: formData.bathrooms === option ? '#4C4848' : '#EDEFF4',
                }}
              >
                <input
                  type="radio"
                  name="bathrooms"
                  value={option}
                  checked={formData.bathrooms === option}
                  onChange={(e) => handleRadioChange('bathrooms', e.target.value)}
                  style={{ marginRight: '8px', accentColor: '#4C4848' }}
                />
                {option}
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '8px',
              fontWeight: '400',
            }}
          >
            Floor number
          </label>
          <input
            type="number"
            placeholder="Enter the floor number of your property"
            value={formData.floorNumber}
            onChange={(e) => handleInputChange('floorNumber', e.target.value)}
            onWheel={(e) => e.currentTarget.blur()}
            onKeyDown={(e) => {
              if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                e.preventDefault()
              }
            }}
            className="number-input-no-spinner"
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '8px',
              fontWeight: '400',
            }}
          >
            Room number
          </label>
          <input
            type="text"
            placeholder="Enter the room number"
            value={formData.roomNumber}
            onChange={(e) => handleInputChange('roomNumber', e.target.value)}
            className="number-input-no-spinner"
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '400',
            }}
          >
            Room position
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {roomPositions.map((option) => (
              <label
                key={option}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: formData.roomPosition === option ? '#4C4848' : '#333',
                  padding: '16px',
                  border: '1px solid #EDEFF4',
                  borderRadius: '8px',
                  backgroundColor: formData.roomPosition === option ? '#F2E9DF' : '#fff',
                  borderColor: formData.roomPosition === option ? '#4C4848' : '#EDEFF4',
                }}
              >
                <input
                  type="radio"
                  name="roomPosition"
                  value={option}
                  checked={formData.roomPosition === option}
                  onChange={(e) => handleRadioChange('roomPosition', e.target.value)}
                  style={{ marginRight: '8px', accentColor: '#4C4848' }}
                />
                {option}
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '400',
            }}
          >
            Does your property has the balcony?
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: '14px',
                color: formData.hasBalcony === true ? '#4C4848' : '#333',
                padding: '16px',
                border: '1px solid #EDEFF4',
                borderRadius: '8px',
                backgroundColor: formData.hasBalcony === true ? '#F2E9DF' : '#fff',
                borderColor: formData.hasBalcony === true ? '#4C4848' : '#EDEFF4',
                flex: 1,
              }}
            >
              <input
                type="radio"
                name="hasBalcony"
                value="true"
                checked={formData.hasBalcony === true}
                onChange={() => handleBooleanChange('hasBalcony', true)}
                style={{ marginRight: '8px', accentColor: '#4C4848' }}
              />
              Yes
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: '14px',
                color: formData.hasBalcony === false ? '#4C4848' : '#333',
                padding: '16px',
                border: '1px solid #EDEFF4',
                borderRadius: '8px',
                backgroundColor: formData.hasBalcony === false ? '#F2E9DF' : '#fff',
                borderColor: formData.hasBalcony === false ? '#4C4848' : '#EDEFF4',
                flex: 1,
              }}
            >
              <input
                type="radio"
                name="hasBalcony"
                value="false"
                checked={formData.hasBalcony === false}
                onChange={() => handleBooleanChange('hasBalcony', false)}
                style={{ marginRight: '8px', accentColor: '#4C4848' }}
              />
              No
            </label>
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '400',
            }}
          >
            Is your property pet-friendly?
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: '14px',
                color: formData.isPetFriendly === true ? '#4C4848' : '#333',
                padding: '16px',
                border: '1px solid #EDEFF4',
                borderRadius: '8px',
                backgroundColor: formData.isPetFriendly === true ? '#F2E9DF' : '#fff',
                borderColor: formData.isPetFriendly === true ? '#4C4848' : '#EDEFF4',
                flex: 1,
              }}
            >
              <input
                type="radio"
                name="isPetFriendly"
                value="true"
                checked={formData.isPetFriendly === true}
                onChange={() => handleBooleanChange('isPetFriendly', true)}
                style={{ marginRight: '8px', accentColor: '#4C4848' }}
              />
              Yes
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: '14px',
                color: formData.isPetFriendly === false ? '#4C4848' : '#333',
                padding: '16px',
                border: '1px solid #EDEFF4',
                borderRadius: '8px',
                backgroundColor: formData.isPetFriendly === false ? '#F2E9DF' : '#fff',
                borderColor: formData.isPetFriendly === false ? '#4C4848' : '#EDEFF4',
                flex: 1,
              }}
            >
              <input
                type="radio"
                name="isPetFriendly"
                value="false"
                checked={formData.isPetFriendly === false}
                onChange={() => handleBooleanChange('isPetFriendly', false)}
                style={{ marginRight: '8px', accentColor: '#4C4848' }}
              />
              No
            </label>
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '8px',
              fontWeight: '400',
            }}
          >
            Maximum guests
          </label>
          <input
            type="number"
            placeholder="Enter no. of guests you allow to stay"
            value={formData.maxGuests}
            onChange={(e) => handleInputChange('maxGuests', e.target.value)}
            onWheel={(e) => e.currentTarget.blur()}
            onKeyDown={(e) => {
              if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                e.preventDefault()
              }
            }}
            className="number-input-no-spinner"
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '400',
            }}
          >
            Availability
          </label>
          <div style={{ marginBottom: '16px' }}>
            {availabilityOptions.map((option) => (
              <label
                key={option}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: formData.availability === option ? '#4C4848' : '#333',
                  marginBottom: '12px',
                  padding: '16px',
                  border: '1px solid #EDEFF4',
                  borderRadius: '8px',
                  backgroundColor: formData.availability === option ? '#F2E9DF' : '#fff',
                  borderColor: formData.availability === option ? '#4C4848' : '#EDEFF4',
                }}
              >
                <input
                  type="radio"
                  name="availability"
                  value={option}
                  checked={formData.availability === option}
                  onChange={(e) => handleRadioChange('availability', e.target.value)}
                  style={{ marginRight: '8px', accentColor: '#4C4848' }}
                />
                {option}
              </label>
            ))}
          </div>

          {formData.availability === 'Available after this date' && (
            <div style={{ marginTop: '16px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  color: '#333',
                  marginBottom: '8px',
                  fontWeight: '400',
                }}
              >
                Date
              </label>
              <div style={{ position: 'relative' }}>
                <DatePicker
                  placeholder="Select available date"
                  value={formData.availableDate}
                  onChange={(date) => handleInputChange('availableDate', date)}
                  style={{
                    width: '100%',
                    height: '48px',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    fontSize: '14px',
                    fontFamily: 'Poppins, sans-serif',
                  }}
                  suffixIcon={
                    <img
                      src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/4YfvPMe8jhCjs9TBTopC65sVavPbL35M9zF8mSEnzktZUbKS3xtmuYTAwFWNUvjYjPcmN4WbVu2duSbr46E6Mct2.png"
                      alt="calendar"
                      style={{ width: '16px', height: '16px' }}
                    />
                  }
                />
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginTop: '32px',
            paddingBottom: '32px',
          }}
        >
          <button
            onClick={onPrevious}
            style={{
              flex: '1',
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: '#666',
              border: '1px solid #e0e0e0',
              borderRadius: '24px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '16px',
              fontFamily: 'Poppins, sans-serif',
              transition: 'all 0.2s ease',
              transform: 'scale(1)',
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.95)'
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            Back
          </button>
          <button
            onClick={onNext}
            disabled={isNextDisabled}
            style={{
              flex: '1',
              padding: '12px 24px',
              backgroundColor: isNextDisabled ? '#ccc' : '#4C4848',
              color: 'white',
              border: 'none',
              borderRadius: '24px',
              cursor: isNextDisabled ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              fontSize: '16px',
              fontFamily: 'Poppins, sans-serif',
              opacity: isNextDisabled ? 0.6 : 1,
              transition: 'all 0.2s ease',
              transform: 'scale(1)',
              boxShadow: !isNextDisabled ? '0 2px 8px rgba(76, 72, 72, 0.3)' : 'none',
            }}
            onMouseDown={(e) => {
              if (!isNextDisabled) {
                e.currentTarget.style.transform = 'scale(0.95)'
              }
            }}
            onMouseUp={(e) => {
              if (!isNextDisabled) {
                e.currentTarget.style.transform = 'scale(1)'
              }
            }}
            onMouseLeave={(e) => {
              if (!isNextDisabled) {
                e.currentTarget.style.transform = 'scale(1)'
              }
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}

export default Step1
